// ═══════════════════════════════════════════════════════════════
// BOUNTIFUL SEA - GAME LOGIC & REDUCER
// ═══════════════════════════════════════════════════════════════

import {
  GameState,
  GameAction,
  GameConfig,
  PlayerSetup,
  Player,
  PlayerId,
  RoundState,
  VoyageState,
  PLAYER_COLORS,
} from '../../types/bountifulSea';
import { rollVoyageDice, getMaxPossibleRoll } from './diceUtils';

// ─────────────────────────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────────────────────────

export function createInitialState(): GameState {
  return {
    phase: 'setup',
    voyageSubPhase: null,
    config: {
      playerCount: 2,
      startingDice: 3,
      anteAmount: 10,
      shipwreckRule: 'depthsClaimAll',
    },
    bountifulSea: 0,
    players: {} as Record<PlayerId, Player>,
    playerOrder: [],
    round: createInitialRoundState(),
    pendingCall: null,
    callWindow: {
      isOpen: false,
      callers: [],
      passedPlayers: [],
    },
    showDice: false,
    animatingDice: false,
    message: null,
    roundHistory: [],
    winner: null,
    finalStandings: null,
  };
}

function createInitialRoundState(): RoundState {
  return {
    roundNumber: 0,
    turnOrder: [],
    currentTurnIndex: 0,
    currentPlayerId: 1,
    playerHauls: {},
    playersWhoMadePort: [],
    playersWhoSank: [],
  };
}

// ─────────────────────────────────────────────────────────────────
// GAME REDUCER
// ─────────────────────────────────────────────────────────────────

export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'START_GAME':
      return handleStartGame(state, action.config, action.players);

    case 'RESET_GAME':
      return createInitialState();

    case 'BEGIN_ROUND':
      return handleBeginRound(state);

    case 'CONFIRM_PLAYER_READY':
      return handleConfirmPlayerReady(state);

    case 'ROLL_DICE':
      return handleRollDice(state);

    case 'SET_DICE_ANIMATION_COMPLETE':
      return { ...state, animatingDice: false };

    case 'DECLARE_TOTAL':
      return handleDeclareTotal(state, action.amount);

    case 'CALL_SCOUNDREL':
      return handleCallScoundrel(state, action.callerId);

    case 'PASS_CALL':
      return handlePassCall(state, action.playerId);

    case 'RESOLVE_CALL':
      return handleResolveCall(state);

    case 'SAIL_ON':
      return handleSailOn(state);

    case 'MAKE_PORT':
      return handleMakePort(state);

    case 'SINK':
      return handleSink(state);

    case 'PROCESS_ROUND_END':
      return handleProcessRoundEnd(state);

    case 'CONFIRM_ROUND_SUMMARY':
      return handleConfirmRoundSummary(state);

    case 'AI_TAKE_ACTION':
      return state; // AI logic handled in hook

    case 'SET_MESSAGE':
      return { ...state, message: action.message };

    case 'HIDE_DICE':
      return { ...state, showDice: false };

    default:
      return state;
  }
}

// ─────────────────────────────────────────────────────────────────
// ACTION HANDLERS
// ─────────────────────────────────────────────────────────────────

function handleStartGame(
  state: GameState,
  config: GameConfig,
  playerSetups: PlayerSetup[]
): GameState {
  // Create players
  const players: Partial<Record<PlayerId, Player>> = {};
  const playerOrder: PlayerId[] = [];

  playerSetups.forEach((setup, index) => {
    const id = (index + 1) as PlayerId;
    playerOrder.push(id);
    players[id] = {
      id,
      name: setup.name || `Player ${id}`,
      color: setup.color || PLAYER_COLORS[index] || '#888888',
      isAI: setup.isAI,
      aiPersonality: setup.isAI ? (setup.aiPersonality || 'balanced') : null,
      stash: 0,
      scoundrelDice: config.startingDice,
      isEliminated: false,
      currentVoyage: null,
    };
  });

  // Calculate initial pot (ante from each player)
  const initialPot = config.anteAmount * config.playerCount;

  // Initialize round state
  const round: RoundState = {
    roundNumber: 1,
    turnOrder: [...playerOrder],
    currentTurnIndex: 0,
    currentPlayerId: playerOrder[0],
    playerHauls: {},
    playersWhoMadePort: [],
    playersWhoSank: [],
  };

  // Initialize player hauls for round
  playerOrder.forEach((id) => {
    round.playerHauls[id] = 0;
  });

  return {
    ...state,
    phase: 'playerTransition',
    voyageSubPhase: null,
    config,
    bountifulSea: initialPot,
    players,
    playerOrder,
    round,
    message: `Round 1 begins! The Bountiful Sea holds ${initialPot} coins.`,
  };
}

function handleBeginRound(state: GameState): GameState {
  const activePlayers = getActivePlayers(state);

  // Rotate turn order (first player goes to end)
  const newTurnOrder = [...state.round.turnOrder];
  if (newTurnOrder.length > 0) {
    const first = newTurnOrder.shift()!;
    newTurnOrder.push(first);
  }

  // Filter out eliminated players
  const filteredTurnOrder = newTurnOrder.filter(
    (id) => !state.players[id]?.isEliminated
  );

  // Reset hauls for new round
  const playerHauls: Partial<Record<PlayerId, number>> = {};
  activePlayers.forEach((p) => {
    playerHauls[p.id] = 0;
  });

  return {
    ...state,
    phase: 'playerTransition',
    voyageSubPhase: null,
    round: {
      roundNumber: state.round.roundNumber + 1,
      turnOrder: filteredTurnOrder,
      currentTurnIndex: 0,
      currentPlayerId: filteredTurnOrder[0],
      playerHauls,
      playersWhoMadePort: [],
      playersWhoSank: [],
    },
    message: `Round ${state.round.roundNumber + 1} begins!`,
  };
}

function handleConfirmPlayerReady(state: GameState): GameState {
  return {
    ...state,
    phase: 'voyage',
    voyageSubPhase: 'rolling',
    showDice: true,
    message: null,
  };
}

function handleRollDice(state: GameState): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  if (!currentPlayer) return state;

  const roll = rollVoyageDice(currentPlayer.scoundrelDice);

  // For continuing voyages, actualTotal = previous actual total + this roll
  // This is the TRUE cumulative sum of all dice rolled (NOT the haul, which is based on declarations!)
  const voyage: VoyageState = currentPlayer.currentVoyage
    ? {
        ...currentPlayer.currentVoyage,
        tideDieValue: roll.tideDie,
        scoundrelDiceValues: roll.scoundrelDice,
        actualTotal: currentPlayer.currentVoyage.actualTotal + roll.total,
        declaredTotal: null,
        rollCount: currentPlayer.currentVoyage.rollCount + 1,
      }
    : {
        tideDieValue: roll.tideDie,
        scoundrelDiceValues: roll.scoundrelDice,
        actualTotal: roll.total, // First roll
        declaredTotal: null,
        haul: 0,
        previousDeclarations: [],
        rollCount: 1,
      };

  return {
    ...state,
    voyageSubPhase: 'declaring',
    animatingDice: true,
    showDice: true,
    players: {
      ...state.players,
      [currentPlayer.id]: {
        ...currentPlayer,
        currentVoyage: voyage,
      },
    },
  };
}

function handleDeclareTotal(state: GameState, amount: number): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  if (!currentPlayer || !currentPlayer.currentVoyage) return state;

  const voyage = currentPlayer.currentVoyage;

  // Validate: must declare at least current haul (can't claim less than what's in your ship)
  // On first roll, haul is 0 so min is effectively 1
  const minRequired = Math.max(1, voyage.haul);
  if (amount < minRequired) return state;

  // Calculate NEW coins to take from pot (declaration minus existing haul)
  // Declaration represents TOTAL claimed haul, not just this roll
  const newCoins = amount - voyage.haul;
  const coinsToTake = Math.min(newCoins, state.bountifulSea);

  // Get other active players who can call
  const otherPlayers = getActivePlayers(state)
    .filter((p) => p.id !== currentPlayer.id)
    .map((p) => p.id);

  const updatedVoyage: VoyageState = {
    ...voyage,
    declaredTotal: amount,
    haul: voyage.haul + coinsToTake,
    previousDeclarations: [...voyage.previousDeclarations, amount],
  };

  return {
    ...state,
    voyageSubPhase: 'awaitingCalls',
    bountifulSea: state.bountifulSea - coinsToTake,
    players: {
      ...state.players,
      [currentPlayer.id]: {
        ...currentPlayer,
        currentVoyage: updatedVoyage,
      },
    },
    callWindow: {
      isOpen: true,
      callers: otherPlayers,
      passedPlayers: [],
    },
    message: `${currentPlayer.name} declares: "The Bountiful Sea grants me ${amount} coins!"`,
  };
}

function handleCallScoundrel(state: GameState, callerId: PlayerId): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  const caller = state.players[callerId];

  if (!currentPlayer || !caller || !currentPlayer.currentVoyage) return state;

  const voyage = currentPlayer.currentVoyage;
  // Calculate this roll's total
  const currentRoll = voyage.tideDieValue + voyage.scoundrelDiceValues.reduce((a, b) => a + b, 0);
  const declaration = voyage.declaredTotal ?? 0;
  // Lying if declaration > currentRoll (you're claiming more than you rolled)
  const wasLying = declaration > currentRoll;

  return {
    ...state,
    voyageSubPhase: 'callResolution',
    pendingCall: {
      callerId,
      targetId: currentPlayer.id,
      wasLying,
      resolved: false,
    },
    callWindow: {
      isOpen: false,
      callers: [],
      passedPlayers: [],
    },
    message: `${caller.name} calls "SCOUNDREL!" on ${currentPlayer.name}!`,
  };
}

function handlePassCall(state: GameState, playerId: PlayerId): GameState {
  const newPassedPlayers = [...state.callWindow.passedPlayers, playerId];
  const remainingCallers = state.callWindow.callers.filter(
    (id) => !newPassedPlayers.includes(id)
  );

  // If all players have passed, move to decision phase
  if (remainingCallers.length === 0) {
    return {
      ...state,
      voyageSubPhase: 'decision',
      callWindow: {
        isOpen: false,
        callers: [],
        passedPlayers: [],
      },
      message: 'No one called Scoundrel. Sail on or make port?',
    };
  }

  return {
    ...state,
    callWindow: {
      ...state.callWindow,
      callers: remainingCallers,
      passedPlayers: newPassedPlayers,
    },
  };
}

function handleResolveCall(state: GameState): GameState {
  const call = state.pendingCall;
  if (!call) return state;

  const target = state.players[call.targetId];
  const caller = state.players[call.callerId];

  if (!target || !caller || !target.currentVoyage) return state;

  if (call.wasLying) {
    // Voyager was lying - caller wins!
    // Voyager loses a die to the caller, haul returns to pot
    const haulReturned = target.currentVoyage.haul;

    const updatedTarget: Player = {
      ...target,
      scoundrelDice: target.scoundrelDice - 1,
      isEliminated: target.scoundrelDice - 1 <= 0,
      currentVoyage: null,
    };

    // Caller gains the die from the liar
    const updatedCaller: Player = {
      ...caller,
      scoundrelDice: caller.scoundrelDice + 1,
    };

    // Handle stash if target eliminated
    let potIncrease = haulReturned; // Haul returns to pot (no +1, die goes to caller)
    if (updatedTarget.isEliminated && state.config.shipwreckRule === 'depthsClaimAll') {
      potIncrease += target.stash;
      updatedTarget.stash = 0;
    }

    const updatedState: GameState = {
      ...state,
      bountifulSea: state.bountifulSea + potIncrease,
      players: {
        ...state.players,
        [target.id]: updatedTarget,
        [caller.id]: updatedCaller,
      },
      pendingCall: null,
      round: {
        ...state.round,
        playerHauls: {
          ...state.round.playerHauls,
          [target.id]: 0, // Haul lost
        },
      },
      message: `CAUGHT! ${target.name} was lying! They lose a Scoundrel Die to ${caller.name}!`,
    };

    // Check if only one player remains - they win immediately
    const victoryState = checkLastPlayerStanding(updatedState);
    if (victoryState) return victoryState;

    return advanceToNextPlayer(updatedState);
  } else {
    // Voyager was truthful - caller loses!
    // Caller loses a die to the target
    const updatedCaller: Player = {
      ...caller,
      scoundrelDice: caller.scoundrelDice - 1,
      isEliminated: caller.scoundrelDice - 1 <= 0,
    };

    // Target gains the die from the false accuser
    const updatedTarget: Player = {
      ...target,
      scoundrelDice: target.scoundrelDice + 1,
    };

    // Handle stash if caller eliminated (no pot increase for dice anymore)
    let potIncrease = 0;
    if (updatedCaller.isEliminated && state.config.shipwreckRule === 'depthsClaimAll') {
      potIncrease += caller.stash;
      updatedCaller.stash = 0;
    }

    const updatedState: GameState = {
      ...state,
      voyageSubPhase: 'decision',
      bountifulSea: state.bountifulSea + potIncrease,
      players: {
        ...state.players,
        [caller.id]: updatedCaller,
        [target.id]: updatedTarget,
      },
      pendingCall: null,
      message: `TRUTH! ${target.name} was honest! ${caller.name} loses a Scoundrel Die to ${target.name}!`,
    };

    // Check if only one player remains - they win immediately
    const victoryState = checkLastPlayerStanding(updatedState);
    if (victoryState) return victoryState;

    return updatedState;
  }
}

function handleSailOn(state: GameState): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  if (!currentPlayer || !currentPlayer.currentVoyage) return state;

  // Check if player CAN sail on (pot not empty)
  if (state.bountifulSea <= 0) {
    return handleMakePort(state);
  }

  // Reset declaredTotal so auto-roll can trigger
  const updatedVoyage: VoyageState = {
    ...currentPlayer.currentVoyage,
    declaredTotal: null, // Clear for new roll
  };

  return {
    ...state,
    voyageSubPhase: 'rolling',
    players: {
      ...state.players,
      [currentPlayer.id]: {
        ...currentPlayer,
        currentVoyage: updatedVoyage,
      },
    },
    message: `${currentPlayer.name} sails on, seeking greater fortune!`,
  };
}

function handleMakePort(state: GameState): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  if (!currentPlayer || !currentPlayer.currentVoyage) return state;

  const haul = currentPlayer.currentVoyage.haul;

  const updatedPlayer: Player = {
    ...currentPlayer,
    stash: currentPlayer.stash + haul,
    currentVoyage: null,
  };

  return advanceToNextPlayer({
    ...state,
    players: {
      ...state.players,
      [currentPlayer.id]: updatedPlayer,
    },
    round: {
      ...state.round,
      playerHauls: {
        ...state.round.playerHauls,
        [currentPlayer.id]: haul,
      },
      playersWhoMadePort: [...state.round.playersWhoMadePort, currentPlayer.id],
    },
    message: `${currentPlayer.name} makes port with ${haul} coins secured!`,
  });
}

function handleSink(state: GameState): GameState {
  const currentPlayer = state.players[state.round.currentPlayerId];
  if (!currentPlayer || !currentPlayer.currentVoyage) return state;

  // Haul returns to pot
  const haulReturned = currentPlayer.currentVoyage.haul;

  const updatedPlayer: Player = {
    ...currentPlayer,
    currentVoyage: null,
  };

  return advanceToNextPlayer({
    ...state,
    bountifulSea: state.bountifulSea + haulReturned,
    players: {
      ...state.players,
      [currentPlayer.id]: updatedPlayer,
    },
    round: {
      ...state.round,
      playerHauls: {
        ...state.round.playerHauls,
        [currentPlayer.id]: 0,
      },
      playersWhoSank: [...state.round.playersWhoSank, currentPlayer.id],
    },
    message: `${currentPlayer.name} sinks! Their haul of ${haulReturned} coins returns to the Bountiful Sea.`,
  });
}

function handleProcessRoundEnd(state: GameState): GameState {
  const activePlayers = getActivePlayers(state);
  if (activePlayers.length <= 1) {
    // Only one player left - they win!
    return declareWinner(state, activePlayers[0]?.id || null);
  }

  // Find player(s) with lowest haul
  const hauls = state.round.playerHauls;
  let minHaul = Infinity;
  activePlayers.forEach((p) => {
    const playerHaul = hauls[p.id] ?? 0;
    if (playerHaul < minHaul) {
      minHaul = playerHaul;
    }
  });

  const losers = activePlayers.filter((p) => (hauls[p.id] ?? 0) === minHaul);
  const loserIds = losers.map((p) => p.id);

  // Each loser loses a die
  const updatedPlayers = { ...state.players };
  let potIncrease = 0;

  loserIds.forEach((id) => {
    const player = updatedPlayers[id];
    if (player) {
      const newDiceCount = player.scoundrelDice - 1;
      const isEliminated = newDiceCount <= 0;

      potIncrease += 1; // Die goes to pot

      if (isEliminated && state.config.shipwreckRule === 'depthsClaimAll') {
        potIncrease += player.stash;
        updatedPlayers[id] = {
          ...player,
          scoundrelDice: 0,
          isEliminated: true,
          stash: 0,
        };
      } else {
        updatedPlayers[id] = {
          ...player,
          scoundrelDice: newDiceCount,
          isEliminated,
        };
      }
    }
  });

  const loserNames = losers.map((p) => p.name).join(' and ');

  return {
    ...state,
    phase: 'roundEnd',
    voyageSubPhase: null,
    bountifulSea: state.bountifulSea + potIncrease,
    players: updatedPlayers,
    roundHistory: [
      ...state.roundHistory,
      {
        roundNumber: state.round.roundNumber,
        hauls: { ...state.round.playerHauls },
        loserPlayerIds: loserIds,
        diceRemoved: loserIds.length,
        potRemaining: state.bountifulSea + potIncrease,
      },
    ],
    message: `${loserNames} had the lowest haul (${minHaul} coins) and ${loserIds.length > 1 ? 'each lose' : 'loses'} a Scoundrel Die!`,
  };
}

function handleConfirmRoundSummary(state: GameState): GameState {
  // Check for victory conditions - pot empty means winner by stash
  if (state.bountifulSea <= 0) {
    return declareWinner(state, findWinnerByStash(state));
  }

  // Check if only one player remains - they win and get the pot
  const victoryState = checkLastPlayerStanding(state);
  if (victoryState) return victoryState;

  // Start new round
  return handleBeginRound(state);
}

// ─────────────────────────────────────────────────────────────────
// HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────────

function getActivePlayers(state: GameState): Player[] {
  return Object.values(state.players).filter((p) => !p.isEliminated);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function _getMinDeclaration(previousDeclarations: number[]): number {
  if (previousDeclarations.length === 0) return 1;
  return Math.max(...previousDeclarations) + 1;
}

function advanceToNextPlayer(state: GameState): GameState {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _activePlayers = getActivePlayers(state);
  const { turnOrder, currentTurnIndex } = state.round;

  // Find next non-eliminated player
  let nextIndex = currentTurnIndex + 1;
  let loopCount = 0;

  while (loopCount < turnOrder.length) {
    if (nextIndex >= turnOrder.length) {
      // Round complete - all players have gone
      return handleProcessRoundEnd(state);
    }

    const nextPlayerId = turnOrder[nextIndex];
    const nextPlayer = state.players[nextPlayerId];

    if (nextPlayer && !nextPlayer.isEliminated) {
      return {
        ...state,
        phase: 'playerTransition',
        voyageSubPhase: null,
        showDice: false,
        round: {
          ...state.round,
          currentTurnIndex: nextIndex,
          currentPlayerId: nextPlayerId,
        },
      };
    }

    nextIndex++;
    loopCount++;
  }

  // All remaining players eliminated - process round end
  return handleProcessRoundEnd(state);
}

function findWinnerByStash(state: GameState): PlayerId | null {
  const players = Object.values(state.players);
  if (players.length === 0) return null;

  let maxStash = -1;
  let maxDice = -1;
  let winnerId: PlayerId | null = null;

  players.forEach((player) => {
    if (
      player.stash > maxStash ||
      (player.stash === maxStash && player.scoundrelDice > maxDice)
    ) {
      maxStash = player.stash;
      maxDice = player.scoundrelDice;
      winnerId = player.id;
    }
  });

  return winnerId;
}

/**
 * Check if only one player remains and award them the pot immediately
 */
function checkLastPlayerStanding(state: GameState): GameState | null {
  const activePlayers = getActivePlayers(state);

  if (activePlayers.length === 1) {
    const winner = activePlayers[0];
    // Award remaining pot to the last player standing
    const updatedWinner: Player = {
      ...winner,
      stash: winner.stash + state.bountifulSea,
    };

    const updatedState: GameState = {
      ...state,
      bountifulSea: 0,
      players: {
        ...state.players,
        [winner.id]: updatedWinner,
      },
    };

    return declareWinner(updatedState, winner.id);
  }

  return null; // No winner yet
}

function declareWinner(state: GameState, winnerId: PlayerId | null): GameState {
  // Sort players by stash (descending), then by dice count
  const sortedPlayers = Object.values(state.players).sort((a, b) => {
    if (b.stash !== a.stash) return b.stash - a.stash;
    return b.scoundrelDice - a.scoundrelDice;
  });

  return {
    ...state,
    phase: 'victory',
    voyageSubPhase: null,
    winner: winnerId,
    finalStandings: sortedPlayers.map((p) => p.id),
    message: winnerId
      ? `${state.players[winnerId]?.name} wins with ${state.players[winnerId]?.stash} coins!`
      : 'The game is over!',
  };
}

// ─────────────────────────────────────────────────────────────────
// EXPORTED HELPERS (for use in hook)
// ─────────────────────────────────────────────────────────────────

export function getMinDeclarationForPlayer(
  player: Player | undefined
): number {
  if (!player?.currentVoyage) return 1;
  // Must declare at least your current haul (can't claim less than what's in your ship)
  // On first roll, haul is 0 so min is 1
  return Math.max(1, player.currentVoyage.haul);
}

export function getMaxDeclarationForPlayer(
  player: Player | undefined
): number {
  if (!player) return 6;
  const maxRoll = getMaxPossibleRoll(player.scoundrelDice);
  if (player.currentVoyage) {
    // Max is the larger of:
    // - actualTotal: so player can always make a truthful declaration
    // - haul + maxRoll: the believable max based on previous claims + this roll's potential
    const believableMax = player.currentVoyage.haul + maxRoll;
    return Math.max(player.currentVoyage.actualTotal, believableMax);
  }
  return maxRoll;
}

export function canPlayerDeclare(
  player: Player | undefined,
  amount: number
): boolean {
  if (!player?.currentVoyage) return false;
  const min = getMinDeclarationForPlayer(player);
  return amount >= min;
}

export function mustPlayerSinkOrLie(player: Player | undefined): boolean {
  if (!player?.currentVoyage) return false;
  // With the haul-based minimum, players can always declare at least their haul
  // Since actualTotal = haul + roll, and roll >= 2, actualTotal >= haul + 2
  // So this is effectively never true in normal gameplay
  const min = Math.max(1, player.currentVoyage.haul);
  return player.currentVoyage.actualTotal < min;
}

export function canPlayerSailOn(
  player: Player | undefined,
  potSize: number
): boolean {
  return potSize > 0 && player?.currentVoyage !== null;
}

export function getActivePlayersFromState(state: GameState): Player[] {
  return getActivePlayers(state);
}
